export interface OfferStatusHistoryItem {
  action: string;
  from_status?: string | null;
  to_status: string;
  user_email: string;
  changed_at: string;
  note?: string | null;
}

export interface Offer {
  id: string;
  tenant_id: string;
  short_code: string;
  tracked_url: string;
  group_tracked_url: string;
  title: string;
  original_url: string;
  affiliate_url?: string | null;
  image_url?: string | null;
  original_price?: number | null;
  promotional_price?: number | null;
  discount_percent?: number | null;
  coupon_code?: string | null;
  coupon_link?: string | null;
  is_coupon_only: boolean;
  categories: string[];
  marketplace: string;
  copy_urgency?: string | null;
  copy_look?: string | null;
  copy_minimalist?: string | null;
  selected_copy?: string | null;
  status: 'pending' | 'queued' | 'sent' | 'archived' | 'draft' | 'error';
  show_in_showcase?: boolean;
  send_to_whatsapp?: boolean;
  link_type?: 'api' | 'marketplace';
  link_marketplace?: string | null;
  is_draft: boolean;
  rating?: number | null;
  commission?: number | null;
  is_full?: boolean;
  quality_score?: number;
  clicks_count: number;
  clicks_by_source: Record<string, number>;
  status_history: OfferStatusHistoryItem[];
  sent_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface OfferScrapeResult {
  url: string;
  title: string;
  image_url?: string | null;
  original_price?: number | null;
  promotional_price?: number | null;
  discount_percent?: number | null;
  marketplace: string;
  categories: string[];
  can_auto_affiliate: boolean;
  affiliate_url?: string | null;
  auto_affiliate_message?: string | null;
  subid?: string | null;
  engine_used?: string | null;
}

export interface AffiliateConvertRequest {
  url: string;
  project_name?: string;
  category_name?: string;
  group_name?: string;
  marketplace?: string;
  custom_tag?: string;
}

export interface AffiliateConvertResponse {
  original_url: string;
  converted_url: string;
  subid: string;
  marketplace: string;
  is_short_link: boolean;
  engine_used: string;
  message?: string | null;
}

export interface OfferCopyGenerateRequest {
  title: string;
  original_price?: number | null;
  promotional_price?: number | null;
  coupon_code?: string | null;
  is_coupon_only?: boolean;
  coupon_rules?: string | null;
  affiliate_url?: string | null;
  marketplace?: string | null;
  categories?: string[];
}

export interface OfferCopyGenerateResult {
  urgency: string;
  look: string;
  minimalist: string;
  source: 'gemini' | 'local_fallback';
}

export interface OfferCreatePayload {
  title: string;
  original_url: string;
  affiliate_url?: string | null;
  image_url?: string | null;
  original_price?: number | null;
  promotional_price?: number | null;
  discount_percent?: number | null;
  coupon_code?: string | null;
  coupon_link?: string | null;
  is_coupon_only?: boolean;
  categories?: string[];
  marketplace?: string;
  copy_urgency?: string | null;
  copy_look?: string | null;
  copy_minimalist?: string | null;
  selected_copy?: string | null;
  status?: 'pending' | 'queued' | 'sent' | 'archived' | 'draft';
  show_in_showcase?: boolean;
  send_to_whatsapp?: boolean;
  link_type?: 'api' | 'marketplace';
  link_marketplace?: string;
  rating?: number | null;
  commission?: number | null;
  is_full?: boolean;
}

export interface OfferUpdatePayload {
  title?: string;
  original_url?: string;
  affiliate_url?: string | null;
  image_url?: string | null;
  original_price?: number | null;
  promotional_price?: number | null;
  discount_percent?: number | null;
  coupon_code?: string | null;
  coupon_link?: string | null;
  is_coupon_only?: boolean;
  categories?: string[];
  marketplace?: string;
  copy_urgency?: string | null;
  copy_look?: string | null;
  copy_minimalist?: string | null;
  selected_copy?: string | null;
  status?: 'pending' | 'queued' | 'sent' | 'archived' | 'draft' | 'error';
  status_note?: string;
  show_in_showcase?: boolean;
  send_to_whatsapp?: boolean;
  link_type?: 'api' | 'marketplace';
  link_marketplace?: string;
  rating?: number | null;
  commission?: number | null;
  is_full?: boolean;
}

export interface OfferBulkActionPayload {
  offer_ids: string[];
  action: 'queue' | 'pause' | 'archive' | 'delete';
  note?: string;
}

export interface OfferBulkActionResult {
  success_count: number;
  failed_count: number;
  errors: string[];
}

export interface OfferMetricsSummary {
  total_offers: number;
  total_clicks: number;
  queued_offers: number;
  sent_offers: number;
  pending_offers?: number;
  archived_offers?: number;
  error_offers?: number;
}

const API_BASE_URL = '';

export async function scrapeOfferUrl(
  token: string,
  url: string
): Promise<OfferScrapeResult> {
  const res = await fetch(`${API_BASE_URL}/api/offers/scrape`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ url }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(
      data?.detail || `Erro ao extrair dados do link (${res.status})`
    );
  }
  return res.json();
}

export async function generateOfferCopies(
  token: string,
  payload: OfferCopyGenerateRequest
): Promise<OfferCopyGenerateResult> {
  const res = await fetch(`${API_BASE_URL}/api/offers/generate-copy`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(
      data?.detail || `Erro ao gerar cópias de copy (${res.status})`
    );
  }
  return res.json();
}

export async function fetchOffers(
  token: string,
  params?: {
    status?: string;
    category?: string;
    min_rating?: number;
    min_commission?: number;
    is_full?: boolean;
    sort_by?: string;
    order?: 'asc' | 'desc';
  }
): Promise<Offer[]> {
  const query = new URLSearchParams();
  if (params?.status) query.append('status', params.status);
  if (params?.category) query.append('category', params.category);
  if (params?.min_rating !== undefined)
    query.append('min_rating', String(params.min_rating));
  if (params?.min_commission !== undefined)
    query.append('min_commission', String(params.min_commission));
  if (params?.is_full !== undefined)
    query.append('is_full', String(params.is_full));
  if (params?.sort_by) query.append('sort_by', params.sort_by);
  if (params?.order) query.append('order', params.order);

  const qs = query.toString() ? `?${query.toString()}` : '';
  const res = await fetch(`${API_BASE_URL}/api/offers${qs}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!res.ok) {
    throw new Error(`Erro ao listar ofertas (${res.status})`);
  }
  return res.json();
}

export async function fetchOfferMetrics(
  token: string
): Promise<OfferMetricsSummary> {
  const res = await fetch(`${API_BASE_URL}/api/offers/metrics`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!res.ok) {
    throw new Error(`Erro ao carregar métricas de ofertas (${res.status})`);
  }
  return res.json();
}

export async function fetchOffer(token: string, id: string): Promise<Offer> {
  const res = await fetch(`${API_BASE_URL}/api/offers/${id}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!res.ok) {
    throw new Error(`Oferta não encontrada (${res.status})`);
  }
  return res.json();
}

export async function createOffer(
  token: string,
  payload: OfferCreatePayload
): Promise<Offer> {
  const res = await fetch(`${API_BASE_URL}/api/offers`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.detail || `Erro ao cadastrar oferta (${res.status})`);
  }
  return res.json();
}

export async function updateOffer(
  token: string,
  id: string,
  payload: OfferUpdatePayload
): Promise<Offer> {
  const res = await fetch(`${API_BASE_URL}/api/offers/${id}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.detail || `Erro ao atualizar oferta (${res.status})`);
  }
  return res.json();
}

export async function deleteOffer(
  token: string,
  id: string
): Promise<{ success: boolean; message: string }> {
  const res = await fetch(`${API_BASE_URL}/api/offers/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.detail || `Erro ao excluir oferta (${res.status})`);
  }
  return res.json();
}

export async function bulkActionOffers(
  token: string,
  payload: OfferBulkActionPayload
): Promise<OfferBulkActionResult> {
  const res = await fetch(`${API_BASE_URL}/api/offers/bulk-action`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(
      data?.detail || `Erro ao executar ação em lote (${res.status})`
    );
  }
  return res.json();
}

export async function convertAffiliateLink(
  token: string,
  payload: AffiliateConvertRequest
): Promise<AffiliateConvertResponse> {
  const res = await fetch(`${API_BASE_URL}/api/offers/convert-link`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(
      data?.detail || `Erro ao converter link de afiliado (${res.status})`
    );
  }
  return res.json();
}

export interface TestAffiliateConnectionPayload {
  marketplace: string;
  app_id?: string;
  secret_key?: string;
  access_token?: string;
  partner_tag?: string;
}

export interface TestAffiliateConnectionResult {
  success: boolean;
  marketplace: string;
  message: string;
  latency_ms?: number;
}

export async function testAffiliateConnection(
  token: string,
  payload: TestAffiliateConnectionPayload
): Promise<TestAffiliateConnectionResult> {
  const res = await fetch(
    `${API_BASE_URL}/api/offers/test-affiliate-connection`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(
      data?.detail || `Erro ao testar conexão de afiliado (${res.status})`
    );
  }
  return res.json();
}
